import type { HistoryManagerState, HistoryStack, Snapshot } from './types';

// Functional History Manager (no classes). Cap size is 20 entries.
const DEFAULT_CAPACITY = 20;

const stacks: HistoryStack = { past: [], future: [] };
let recipePoolId: string | null = null;
let restoring = false;

/** Returns whether a restore is in progress */
const isRestoring = (): boolean => restoring;

/** Clears stacks and binds to the provided recipe */
const clear = (poolId: string): void => {
  recipePoolId = poolId;
  stacks.past = [];
  stacks.future = [];
};

/** Captures a new snapshot; clears redo stack; enforces capacity */
const capture = (poolId: string, snapshot: Snapshot): void => {
  if (!recipePoolId) {
    recipePoolId = poolId;
  }
  if (recipePoolId !== poolId) {
    // Single active recipe assumption: ignore other ids
    return;
  }

  stacks.past.push(snapshot);

  if (stacks.past.length > DEFAULT_CAPACITY) {
    stacks.past.shift();
  }

  stacks.future = [];
};

/** Steps back in history if possible and returns target snapshot */
const undo = (): Snapshot | null => {
  if (stacks.past.length <= 1) {
    return null;
  }

  const current = stacks.past.pop() as Snapshot;
  stacks.future.push(current);

  const target = stacks.past[stacks.past.length - 1] ?? null;
  return target ?? null;
};

/** Steps forward in history if possible and returns target snapshot */
const redo = (): Snapshot | null => {
  if (stacks.future.length === 0) {
    return null;
  }

  const next = stacks.future.pop() as Snapshot;
  stacks.past.push(next);
  return next;
};

/** Sets restoring mode to avoid snapshot capture during reconciliation */
const setRestoring = (value: boolean): void => {
  restoring = value;
};

/** Returns a minimal public state for UI/controls */
const getState = (): HistoryManagerState => ({
  recipePoolId,
  canUndo: stacks.past.length > 1,
  canRedo: stacks.future.length > 0,
  pastSize: stacks.past.length,
  futureSize: stacks.future.length,
});

const historyManager = {
  isRestoring,
  clear,
  capture,
  undo,
  redo,
  setRestoring,
  getState,
};

export default historyManager;


